<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { ROUTES } from "@utils/routes.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import Header from "@components/Header.svelte";
  import { ToastNotification } from "carbon-components-svelte";
  import { fly } from "svelte/transition";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import WorkshopColumn from "@components/WorkshopColumn.svelte";
  import { InfoBlock } from "paxray-components-svelte";
  import { DOWNLOAD_ICON, OPEN_IN_NEW_ICON } from '@utils/icons.js';

  const urlParams = useUrlParams();
  const step = PROJECT_STAGES.SETUP.steps.WORKSHOP;
  const stage = PROJECT_STAGES.SETUP.id;

  let toastMsg;
  let saveSuccessful;
  let showWorkshopGuide = false;
  let workshopGuideStep = 0;
  let workshopGuideSteps = [];

  onMount(() =>
    registerViewTour(() => {
      workshopGuideSteps = [
        {
          target: '[data-guide="workshop-resources"]',
          title: "Align your team in the setup workshop",
          text: "After defining your project scope and setting up your teams, use the workshop to align participants on project goals and how to take part.",
        },
        {
          target: '[data-guide="workshop-downloads"] .workshop_column_items',
          title: "Prepare your workshop materials",
          text: "Use the workshop slides to explain the project and the task registration guide to introduce the client. The slides are currently being updated and will be available soon.",
        },
        {
          target:
            '[data-guide="workshop-project-links"] .workshop_column_items',
          title: "Share the participant project link",
          text: "Participants can use this link to add their own restrictions on data collection. Open it to review the participant view, or use Suggest Message to get a ready to send message for your participants.",
        },
        {
          target:
            '[data-guide="workshop-external-resources"] .workshop_column_items',
          title: "Find help with task registration",
          text: "The Help Center provides a short video and FAQs for the later task registration phase. Open it yourself, or use Suggest Message to share it with your participants.",
        },
        {
          target:
            '[data-guide="complete-workshop"] > button, [data-guide="complete-workshop"] > .completed-badge',
          title: "Complete and lock the workshop",
          text: 'Only use "Complete & Lock Workshop" after the workshop has taken place and participants have received the relevant information. Completing this step locks it and cannot be undone. Once complete, the button is replaced by "Completed". The next setup task is "Exclude Apps & Websites".',
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      workshopGuideStep = 0;
      showWorkshopGuide = workshopGuideSteps.length > 0;
    }),
  );

  let downloads = [
    {
      id: 1,
      name: "Workshop slides",
      description:
        "Slides for the workshop with your participants. They explain what the project is about, how data is collected and what each participant needs to do.",
      type: "PDF",
      icon: "slideshow",
      actionIcon: DOWNLOAD_ICON,
      buttonText: "Download Slides",
      file: "/downloads/workshop/workshop-slides.pdf",
      invitationButtonText: "Suggest Message",
      // The slides go out as an attachment, so the message carries no link
      invitation: () => ({
        heading: "Suggested Message",
        subject: "Slides from our paxray workshop",
        message: `Hello,

Thank you for joining our workshop for the Task Mining project with paxray. With paxray we want to better understand how our daily work is done, so we can improve our processes.

Attached you will find the slides we went through together. They explain what the project is about, how data is collected and what you need to do.

If you have any questions, feel free to reach out.

Thank you!`,
      }),
      // Remove status and unavailable once the updated slides are in place.
      status: "Update in progress",
      unavailable: true,
    },
    {
      id: 2,
      name: "Task registration guide",
      description:
        "A short guide that shows participants how to register their tasks in the client. Share it after the workshop so everyone can look it up later.",
      type: "PDF",
      icon: "developer_guide",
      actionIcon: DOWNLOAD_ICON,
      buttonText: "Download Guide",
      file: "/downloads/workshop/task-registration-guide.pdf",
      invitationButtonText: "Suggest Message",
      // The guide goes out as an attachment, so the message carries no link
      invitation: () => ({
        heading: "Suggested Message",
        subject: "How to register your tasks in paxray",
        message: `Hello,

In the next phase of our Task Mining project with paxray you will register your tasks in the paxray client. Attached you will find a short guide that shows you step by step how this works.

Please keep it at hand during the registration phase.

Thank you!`,
      }),
    },
  ];

  $: projectLink = `/UserInput?idproject=${idproject}&currentStep=0`;
  $: projectsLinks = [
    {
      id: 1,
      name: "Participant Project Link",
      description:
        "Participants use this link to tell you what should not be collected about them. Send it to all participants before the workshop so their input is in place before data collection starts.",
      type: "Platform Link",
      icon: "open_in_new",
      actionIcon: OPEN_IN_NEW_ICON,
      url: projectLink,
      buttonText: "Open Participant Link",
      invitationButtonText: "Suggest Message",
      invitation: (link) => ({
        heading: "Suggested Message",
        subject: "Your input for our Task Mining project with paxray",
        message: `Hello,

We will soon start a Task Mining project with paxray. With paxray we want to better understand how our daily work is done, so we can improve our processes. Before data collection starts, we want to make sure it respects your preferences.

Please open the link below. It takes you to the paxray platform, where you can tell us what should not be collected about you. It only takes a few minutes.

${link}

Please complete it before data collection starts.

Thank you!`,
      }),
    },
  ];

  let externalResources = [
    {
      id: 1,
      name: "Task Registration Help Center",
      description:
        "A short video and answers to common questions about task registration. Share it with participants so they can find help on their own during the registration phase.",
      type: "External Link",
      icon: "open_in_new",
      actionIcon: OPEN_IN_NEW_ICON,
      url: "https://paxray.notion.site/task-registration-guide",
      buttonText: "Open Help Center",
      invitationButtonText: "Suggest Message",
      invitation: (link) => ({
        heading: "Suggested Message",
        subject: "Help for registering your tasks in paxray",
        message: `Hello,

In the next phase of our Task Mining project with paxray you will register your tasks in the paxray client. The paxray Help Center explains how this works with a short video and answers to common questions.

${link}

If anything is still unclear, feel free to reach out.

Thank you!`,
      }),
    },
  ];

  $: ({ idproject } = $urlParams);
  $: projectProgressHref = `${ROUTES.dam.progress}?${new URLSearchParams({ idproject }).toString()}`;
  $: resourceSections = [
    {
      id: "downloads",
      title: "Downloads",
      accentColor: "#b7e3bd",
      tooltipText: "Workshop presentations and guides.",
      resources: downloads,
      handleAction: (resource) => window.open(resource.file, "_blank"),
    },
    {
      id: "project-links",
      title: "Project Links",
      accentColor: "#abcfe8",
      tooltipText: "Links for project participants.",
      resources: projectsLinks,
      handleAction: (resource) => window.open(resource.url, "_blank"),
    },
    {
      id: "external-resources",
      title: "External Resources",
      accentColor: "#e5e8a8",
      tooltipText: "Additional workshop resources.",
      resources: externalResources,
      handleAction: (resource) => window.open(resource.url, "_blank"),
    },
  ];

  $$restProps;
</script>

<Header
  section="Project"
  area="Setup"
  breadcrumb="Inform Participants"
  areaHref={`${projectProgressHref}#setup-workshop`}
  tooltipText="Prepare and run the workshop with your team. After completion, this step cannot be changed."
>
  <div data-guide="complete-workshop">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      bind:toastMsg
      bind:saveSuccessful
      text="Complete & Lock Workshop"
      size="sm"
      completionWarning="After completion, the workshop step cannot be changed. Please make sure the workshop has been prepared and completed before continuing. This action cannot be undone."
    />
  </div>
</Header>

<div class="main">
  <div data-guide="workshop-resources">
    <InfoBlock
      type="info"
      heading="Workshop resources"
      message="Use the resources below to prepare and run the workshop. Once participants have received the relevant information, click &quot;Complete &amp; Lock Workshop&quot;."
      seamless={true}
      showClose={true}
    />
  </div>
  <div class="resource_grid">
    {#each resourceSections as section}
      <section class="resource_section" data-guide={`workshop-${section.id}`}>
        <WorkshopColumn
          headerName={section.title}
          resources={section.resources}
          accentColor={section.accentColor}
          tooltipText={section.tooltipText}
          id={section.id}
          handleAction={section.handleAction}
        />
      </section>
    {/each}
  </div>
</div>

<SpotlightGuide
  bind:open={showWorkshopGuide}
  bind:currentStep={workshopGuideStep}
  steps={workshopGuideSteps}
  allowInteraction={false}
/>

{#if toastMsg}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:60px; right:20px; z-index:100;"
  >
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={toastMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
        toastMsg = null;
        saveSuccessful = false;
      }}
    />
  </div>
{/if}

<style>
  .main {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    width: 100%;
    min-height: calc(100vh - 4rem);
    background: var(--pax-white, #f9fbff);
  }

  .resource_grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
    gap: 0;
    flex: 1;
    min-height: 0;
  }

  .resource_section {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
    border-left: 1px solid var(--border-color, #d9d9d9);
  }

  .resource_section:first-child {
    border-left: 0;
  }

  .resource_section :global(.seamless_grid_header_value) {
    background: var(--pax-black, #1c1c20);
    color: var(--pax-white, #fff);
  }

  @media (max-width: 64rem) {
    .resource_grid {
      grid-template-columns: repeat(3, minmax(18rem, 1fr));
    }
  }

  @media (max-width: 48rem) {
    .resource_grid {
      grid-template-columns: 1fr;
    }

    .resource_section {
      border-left: 0;
      border-bottom: 1px solid var(--border-color, #d9d9d9);
    }
  }
</style>
